import type { SidebarNav, SidebarSubItem } from '@elva/ui';

/**
 * Left-menu order (spec 47): groups ai · day · comms · agents · review — a divider is
 * drawn on each change. AI Team sits in the agents group, ahead of Recall.
 * Counts: `alert` (red) = a person must act; `muted` (grey) = information.
 * Triage / Email / Messages counts come from the server (useSidebarCounts in AppLayout).
 * The agents group is otherwise in lifecycle order:
 * due for a visit (Recall) → diagnosed not scheduled (Treatment Intelligence) →
 * treated not paid (Collections).
 * Collections is rendered locked (not-purchased) until the A/R agent is bought.
 * "Not permitted" items are omitted entirely (never rendered) — handled upstream.
 */
/**
 * AI Team sublist (menu spec §10). Order matches the roster page, not the main
 * menu. Status drives the trailing dot: on=green · off=grey · paused=amber
 * (held/safe mode) · locked=padlock (not purchased). "Accounts receivable" is the
 * agent; "Collections" (top-level) is its work surface.
 */
// The seven sublist rows, in the roster's order (spec 36 §7 rev 3 / spec 47 §3).
// AI SMS and AI Email are not here — they're reached from Messages and Email, and
// their cards stay on /ai-team. Status is overridden at render from the hold + plan
// store (AppLayout): green on · amber paused · padlock locked.
export const AI_TEAM_CHILDREN: SidebarSubItem[] = [
  { key: 'receptionist', label: 'AI Receptionist', status: 'on' },
  { key: 'gap-filler', label: 'Gap Filler', status: 'on' },
  { key: 'treatment', label: 'Treatment Coordinator', status: 'on' },
  { key: 'invisalign', label: 'Invisalign Coordinator', status: 'on' },
  { key: 'ar', label: 'Accounts receivable', status: 'on' },
  { key: 'recall', label: 'Recall', status: 'on' },
  { key: 'reminders', label: 'Appointment Reminders', status: 'on' },
];

/** Child key → route. */
export const AI_TEAM_CHILD_PATHS: Record<string, string> = {
  receptionist: '/ai-team/receptionist',
  sms: '/messages',
  email: '/email',
  'gap-filler': '/ai-team/gap-filler',
  treatment: '/ai-team/treatment',
  invisalign: '/ai-team/invisalign',
  ar: '/ai-team/ar',
  recall: '/ai-team/recall',
  reminders: '/ai-team/reminders',
};

/** The active AI Team child key for a path under /ai-team/<child>, else undefined. */
export function getAiTeamChildFromPath(pathname: string): string | undefined {
  const m = /^\/ai-team\/([^/]+)/.exec(pathname);
  return m ? m[1] : undefined;
}

export const SIDEBAR_NAV: SidebarNav[] = [
  // Dashboard (spec 17 §2): a new row at the VERY TOP of the rail, above AI Team,
  // and the app's landing route. No badge — a count here would compete with the
  // queues below it, and the page's own tiles are the count.
  { key: 'dashboard', icon: 'grid', label: 'Dashboard', group: 'ai' },
  // Group 1 — the day
  { key: 'calendar', icon: 'calendar', label: 'Calendar', group: 'day' },
  { key: 'bird-eye', icon: 'eye', label: 'Bird Eye View', group: 'day' },
  { key: 'triage', icon: 'alert', label: 'Triage Inbox', group: 'day', countTone: 'alert' },
  // Calls (spec 31): one list for every call a person makes. Directly under Triage.
  // The badge is calls due today (grey / muted — it's a worklist count, not a red alert).
  { key: 'calls', icon: 'phone', label: 'Calls', group: 'day', count: 11, countTone: 'muted' },
  // Warm transfer (rev 5): under Triage — both are red queues where a person is
  // needed. Count is patients holding for a person (red, must update live at
  // runtime). Conditional on the inbound-phone add-on (purchased in this demo).
  {
    key: 'warm-transfer',
    icon: 'phone-out',
    label: 'Warm transfer',
    group: 'day',
    count: 3,
    countTone: 'alert',
  },
  { key: 'patients', icon: 'patients', label: 'Patients', group: 'day' },
  // Group 2 — conversations and paperwork
  // Counts are UNREAD threads (fix-list #9), reconciled with each inbox's own
  // "Unread" figure so a badge nobody can reconcile doesn't teach staff to
  // ignore every count. Server-driven: email_unread_count / messages_unread_count
  // (useSidebarCounts in AppLayout) are already unread-thread counts by name.
  { key: 'email', icon: 'mail', label: 'Email', group: 'comms', countTone: 'muted' },
  { key: 'messages', icon: 'chat', label: 'Messages', group: 'comms', countTone: 'muted' },
  { key: 'forms', icon: 'doc', label: 'Forms', group: 'comms' },
  // Online bookings (spec 50 §3): a read-only record of the public booking page.
  // After Forms. Shown only when the add-on is bought (filtered in AppLayout).
  // No badge — work to do is counted by Triage Inbox.
  { key: 'online-bookings', icon: 'globe', label: 'Online bookings', group: 'comms' },
  // Group 3 — AI Team, then the money already in the practice, in lifecycle order.
  // AI Team moved here from 2nd place (spec 47). No badge (spec 36 §7). The children
  // carry per-agent status dots (set live in AppLayout).
  {
    key: 'ai-team',
    icon: 'team',
    label: 'AI Team',
    group: 'agents',
    children: AI_TEAM_CHILDREN,
  },
  // (Reminders has no top-level item: confirmation status lives in Calendar and
  // Patients; the agent's own page is under AI Team — spec 03 §2.)
  { key: 'recall', icon: 'refresh', label: 'Recall', group: 'agents' },
  // Treatment Intelligence (rev 8 §2.4): a menu item, NOT a page under AI Team —
  // it answers "how much money is in my PMS, worth an hour?" and survives the
  // agent being switched off. No count (§4.2): a number would read as a queue.
  // Route is /treatment-intelligence for now (the public patient page owns
  // /treatment); the full page is spec 07.
  { key: 'treatment-intel', icon: 'gem', label: 'Treatment Intelligence', group: 'agents' },
  // Payment links (spec 12) — the till, not the A/R work surface. Conditional on
  // the A/R add-on; unlocked in this prototype so the built page is reachable.
  { key: 'collections', icon: 'credit', label: 'Collections', group: 'agents' },
  // Group 4 — review and setup.
  // Reports is dropped from V1 (P352): no section, no pages, no rail row, no
  // redirect and no placeholder. The Dashboard, the per-module surfaces and the
  // Communication log cover it between them — and the log is now the only record
  // surface, which is why it grew a direction filter and a CSV export.
  { key: 'communications', icon: 'list', label: 'Communication log', group: 'review' },
  { key: 'settings', icon: 'cog', label: 'Settings', group: 'review' },
];

export const NAV_PATHS: Record<string, string> = {
  dashboard: '/dashboard',
  'ai-team': '/ai-team',
  calendar: '/calendar',
  'bird-eye': '/bird-eye',
  triage: '/triage',
  calls: '/calls',
  'warm-transfer': '/warm-transfer',
  patients: '/patients',
  email: '/email',
  messages: '/messages',
  forms: '/forms',
  'online-bookings': '/online-bookings',
  recall: '/recall',
  'treatment-intel': '/treatment-intelligence',
  collections: '/collections',
  communications: '/communications',
  settings: '/settings',
};

/** Path prefixes that map to a nav key (longest-prefix wins for nested routes). */
const PREFIX_TO_KEY: [string, string][] = [
  ['/dashboard', 'dashboard'],
  ['/ai-team', 'ai-team'],
  ['/calendar', 'calendar'],
  ['/bird-eye', 'bird-eye'],
  ['/triage', 'triage'],
  ['/calls', 'calls'],
  ['/warm-transfer', 'warm-transfer'],
  ['/patients', 'patients'],
  ['/email', 'email'],
  ['/messages', 'messages'],
  ['/forms', 'forms'],
  ['/online-bookings', 'online-bookings'],
  ['/recall', 'recall'],
  ['/treatment-intelligence', 'treatment-intel'],
  ['/collections', 'collections'],
  ['/communications', 'communications'],
  ['/settings', 'settings'],
];

export function getNavKeyFromPath(pathname: string): string {
  for (const [prefix, key] of PREFIX_TO_KEY) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
  }
  // `/` redirects to /dashboard (spec 17 §2), so the fallback follows it rather
  // than lighting up Calendar on a route that no longer exists.
  return 'dashboard';
}
